import { useCallback, useMemo, useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react'
import { languages as allLanguages, LanguageCode } from '@/config/languages'
import { cn } from '@/lib/utils'
import { useShop } from '@/context/shop-context'
import { Sparkles } from 'lucide-react'

type Props = {
  value: Record<string, string>
  onChange: (val: Record<string, string>) => void
  requiredLangs?: string[]
  /**
   * Languages to display. If omitted, active shop languages are used.
   */
  languages?: string[]
  placeholder?: string
  className?: string
  disabled?: boolean
  onDescriptionGenerated?: (description: Record<string, string>) => void
  onTranslate?: (value: Record<string, string>) => Promise<Record<string, string> | void>
  onGenerateDescription?: (value: Record<string, string>) => Promise<Record<string, string> | void>
  enableTranslate?: boolean
  enableGenerateDescription?: boolean
  activeLang?: LanguageCode
  onActiveLangChange?: (lang: LanguageCode) => void
}

const MultiLanguageInput = forwardRef<HTMLInputElement, Props>(({
  value = {},
  onChange,
  requiredLangs = [],
  languages,
  placeholder = '',
  className,
  disabled = false,
  onDescriptionGenerated,
  onTranslate,
  onGenerateDescription,
  enableTranslate = false,
  enableGenerateDescription = false,
  activeLang: activeLangProp,
  onActiveLangChange,
}, ref) => {
  const { activeShop } = useShop()
  const shopLangs = activeShop?.settings?.languages ?? []

  const baseLangs = languages ?? shopLangs

  const langCodes = useMemo(() => {
    const base = baseLangs || []
    const combined = [...base]
    // Add required languages that aren't already in base, preserving base order
    requiredLangs.forEach((lang) => {
      if (!combined.includes(lang)) {
        combined.push(lang)
      }
    })
    return combined
  }, [baseLangs, requiredLangs])

  const finalLangCodes = langCodes.length > 0 ? langCodes : ['en']

  const langs = useMemo(
    () =>
      finalLangCodes
        .map((code) => allLanguages.find((l) => l.code === code))
        .filter((l): l is (typeof allLanguages)[number] => l !== undefined),
    [finalLangCodes]
  )

  const initialLang: LanguageCode = (langs[0]?.code as LanguageCode) || 'en'
  const [internalActiveLang, setInternalActiveLang] =
    useState<LanguageCode>(initialLang)
  const isControlled = activeLangProp !== undefined
  const activeLang = isControlled ? activeLangProp : internalActiveLang
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const selectActiveLang = useCallback(
    (lang: LanguageCode) => {
      if (!isControlled) {
        setInternalActiveLang(lang)
      }
      onActiveLangChange?.(lang)
    },
    [isControlled, onActiveLangChange]
  )

  // Update active language if it's no longer available (uncontrolled only)
  useEffect(() => {
    if (isControlled) return
    if (langs.length > 0 && !langs.find((l) => l.code === internalActiveLang)) {
      setInternalActiveLang(langs[0].code as LanguageCode)
    }
  }, [langs, internalActiveLang, isControlled])

  const [translateLoading, setTranslateLoading] = useState(false)
  const [translateError, setTranslateError] = useState(false)
  const [descLoading, setDescLoading] = useState(false)
  const [descError, setDescError] = useState(false)

  const hasName = useMemo(
    () => Object.values(value || {}).some((v) => v && v.trim() !== ''),
    [value]
  )
  const showAISection = (enableTranslate || enableGenerateDescription) && !disabled

  const handleChange = useCallback(
    (lang: LanguageCode, text: string) => {
      onChange({ ...value, [lang]: text })
    },
    [onChange, value]
  )

  const hasError = useCallback(
    (lang: string) =>
      requiredLangs.includes(lang) &&
      (!value || !value[lang] || value[lang].trim() === ''),
    [requiredLangs, value]
  )

  useImperativeHandle(ref, () => ({
    focus: () => {
      const firstErrorLang = langs.find(l => hasError(l.code))
      const targetLang = firstErrorLang ? (firstErrorLang.code as LanguageCode) : activeLang
      
      if (targetLang !== activeLang) {
        selectActiveLang(targetLang)
      }
      
      // Use setTimeout to ensure the tab switch has happened and input is visible
      setTimeout(() => {
        inputRefs.current[targetLang]?.focus()
      }, 0)
    }
  }) as any)

  const handleTranslate = useCallback(async () => {
    if (!hasName || translateLoading || !onTranslate) return

    setTranslateLoading(true)
    try {
      const result = await onTranslate(value)
      if (result) {
        onChange(result)
      }
    } catch (e: any) {
      setTranslateError(true)
      setTimeout(() => setTranslateError(false), 300)
    } finally {
      setTranslateLoading(false)
    }
  }, [hasName, translateLoading, onTranslate, value, onChange])

  const handleGenerateDesc = useCallback(async () => {
    if (!hasName || descLoading || !onGenerateDescription) return

    setDescLoading(true)
    try {
      const result = await onGenerateDescription(value)
      if (result && onDescriptionGenerated) {
        onDescriptionGenerated(result)
      }
    } catch (e: any) {
      setDescError(true)
      setTimeout(() => setDescError(false), 300)
    } finally {
      setDescLoading(false)
    }
  }, [descLoading, hasName, onGenerateDescription, onDescriptionGenerated, value])

  return (
    <div className={className}>
      <div
        className={cn(
          'rounded-md border bg-card shadow-sm',
          disabled && 'pointer-events-none opacity-70'
        )}
      >
        {/* Language Tabs */}
        <div className="flex border-b bg-muted/40">
          {langs.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => selectActiveLang(lang.code as LanguageCode)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors',
                activeLang === lang.code
                  ? 'border-b-2 border-primary bg-card text-foreground'
                  : 'border-b-2 border-transparent text-muted-foreground hover:bg-muted/40'
              )}
            >
              <span>{lang.flag}</span>
              {lang.label}
              {hasError(lang.code) && (
                <span className="text-destructive">*</span>
              )}
            </button>
          ))}
        </div>

        {/* Inputs */}
        <div className="p-4">
          {langs.map((lang) => (
            <div
              key={lang.code}
              className={activeLang === lang.code ? 'block' : 'hidden'}
            >
              <input
                ref={(el) => {
                    if (el) inputRefs.current[lang.code] = el;
                }}
                type="text"
                lang={lang.code}
                disabled={disabled}
                className={cn(
                  'w-full rounded-md border bg-card px-3 py-2 text-sm ring-offset-background',
                  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  hasError(lang.code)
                    ? 'border-destructive focus-visible:ring-destructive'
                    : 'border-input',
                  disabled && 'cursor-not-allowed opacity-70'
                )}
                value={value[lang.code] || ''}
                onChange={(e) => handleChange(lang.code, e.target.value)}
                placeholder={`${placeholder} (${lang.label})`}
              />
              {hasError(lang.code) && (
                <p className="mt-1 text-xs font-medium text-destructive">
                  This field is required.
                </p>
              )}
            </div>
          ))}
        </div>

        {/* AI Assistance Panel */}
        {showAISection && (
          <div className="border-t bg-muted/20 px-4 py-3">
            <div className="flex flex-col gap-3 rounded-lg border bg-card px-4 py-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">AI Assistance</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Quickly translate this name and generate multilingual
                  descriptions with one click.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {enableTranslate && (
                  <button
                    type="button"
                    onClick={handleTranslate}
                    disabled={
                      !hasName ||
                      translateLoading ||
                      disabled
                    }
                    aria-busy={translateLoading}
                    className={cn(
                      'inline-flex items-center justify-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-xs font-medium transition-transform transition-colors',
                      'border-primary text-primary hover:bg-primary/10',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      translateError && 'animate-shake',
                      translateLoading && 'opacity-70'
                    )}
                  >
                    {translateLoading && (
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    )}
                    <span>Translate to all languages</span>
                  </button>
                )}

                {enableGenerateDescription && (
                  <button
                    type="button"
                    onClick={handleGenerateDesc}
                    disabled={!hasName || descLoading || disabled}
                    aria-busy={descLoading}
                    className={cn(
                      'inline-flex items-center justify-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-xs font-medium transition-transform transition-colors',
                      'border-muted-foreground text-muted-foreground hover:bg-muted/40',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      descError && 'animate-shake',
                      descLoading && 'opacity-70'
                    )}
                  >
                    {descLoading && (
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                    )}
                    <span>Generate description</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

MultiLanguageInput.displayName = "MultiLanguageInput"

export default MultiLanguageInput
